import { useMemo, useState } from "react";
import { useStore } from "../state/store";
import { validateProject, hasBlockingErrors } from "../model/validation";
import { buildExportFiles } from "../export/files";
import { checkGuideConsistency } from "../export/consistency";
import { exportProject, ExportBlockedError, ConsistencyError } from "../export/index";
import { IssueList } from "../ui/common";
import { downloadBlob } from "../ui/files";

export function ExportPanel() {
  const project = useStore((s) => s.project);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const issues = useMemo(() => validateProject(project), [project]);
  const blocking = hasBlockingErrors(issues);

  // Build files for preview + the consistency check (does not throw).
  const files = useMemo(() => {
    try {
      return buildExportFiles(project);
    } catch {
      return [];
    }
  }, [project]);

  const consistency = useMemo(() => checkGuideConsistency(project, files), [project, files]);

  const current = files.find((f) => f.path === selectedFile) ?? files.find((f) => f.path.endsWith("main.js"));

  const doExport = () => {
    setMessage(null);
    try {
      const result = exportProject(project);
      downloadBlob(result.blob, result.filename);
      setMessage(`Exported ${result.filename} (${result.files.length} files).`);
    } catch (e) {
      if (e instanceof ExportBlockedError) setMessage("Fix the validation errors above before exporting.");
      else if (e instanceof ConsistencyError) setMessage(`Consistency gate failed: ${e.message}\n${e.details.join("\n")}`);
      else setMessage(String(e));
    }
  };

  return (
    <div className="panel split">
      <div className="list-column wide">
        <h2>Export</h2>

        <section className="card">
          <h3>Validation</h3>
          <IssueList issues={issues} />
        </section>

        <section className="card">
          <h3>Cross-file consistency gate</h3>
          <p className={consistency.ok ? "ok" : "error-text"}>{consistency.message}</p>
          {consistency.details.length > 0 && (
            <pre className="details">{consistency.details.join("\n")}</pre>
          )}
          <p className="hint">
            Hard gate: the guide item id must be byte-identical in <code>items/{project.guideItemId}.json</code>,{" "}
            <code>scripts/main.js</code>, and the guide recipe result.
          </p>
        </section>

        {project.importedLang && Object.keys(project.importedLang).length > 0 && (
          <section className="card">
            <h3>Imported translations</h3>
            <p className="hint">
              This project was imported, so export <strong>merges</strong> into the original lang files
              ({Object.keys(project.importedLang).length} locale{Object.keys(project.importedLang).length === 1 ? "" : "s"}).
              Your generated keys are updated in place, and every other translation is preserved.
            </p>
          </section>
        )}

        <section className="card">
          <button className="btn primary big" disabled={blocking || !consistency.ok} onClick={doExport}>
            Download {project.namespace}.mcaddon
          </button>
          {(blocking || !consistency.ok) && <p className="hint">Resolve the issues above to enable export.</p>}
          {message && <pre className="export-message">{message}</pre>}
        </section>

        <section className="card">
          <h3>Files ({files.length})</h3>
          <ul className="file-tree">
            {files.map((f) => (
              <li
                key={f.path}
                className={f.path === current?.path ? "active" : ""}
                onClick={() => setSelectedFile(f.path)}
              >
                {f.path}
                {typeof f.content !== "string" && <span className="muted"> (binary)</span>}
              </li>
            ))}
          </ul>
        </section>
      </div>

      <div className="editor-column">
        <h3>Preview</h3>
        {current ? (
          <>
            <div className="preview-path">{current.path}</div>
            <pre className="file-preview">
              {typeof current.content === "string" ? current.content : `[binary: ${current.content.length} bytes]`}
            </pre>
          </>
        ) : (
          <p className="muted">Select a file to preview.</p>
        )}
      </div>
    </div>
  );
}
